import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Award, TrendingUp, Users, DollarSign, ArrowRight } from 'lucide-react';

const CaseStudiesPage = () => {
  const caseStudies = [
    {
      company: 'Global Manufacturing Corp',
      industry: 'Manufacturing',
      logo: '/api/placeholder/120/60',
      challenge: 'Struggling with unplanned equipment downtime costing $50K per hour and paper-based maintenance processes causing inefficiencies.',
      solution: 'Implemented mwbTechs CMMS with predictive maintenance scheduling, mobile work orders, and IoT sensor integration.',
      results: [
        { icon: TrendingUp, value: '35%', label: 'Reduction in Downtime' },
        { icon: DollarSign, value: '$2M', label: 'Annual Savings' },
        { icon: Users, value: '40%', label: 'Productivity Increase' },
      ],
      testimonial: 'mwbTechs transformed our maintenance operations. The ROI was visible within 3 months.',
      author: 'John Smith, Operations Director',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      company: 'Retail Solutions Inc',
      industry: 'Retail',
      logo: '/api/placeholder/120/60',
      challenge: 'Disconnected systems across 200+ stores and poor customer service due to lack of 24/7 support.',
      solution: 'Deployed AI chatbots across all digital channels with unified customer data platform and real-time inventory integration.',
      results: [
        { icon: TrendingUp, value: '25%', label: 'Sales Increase' },
        { icon: Users, value: '80%', label: 'Queries Automated' },
        { icon: DollarSign, value: '60%', label: 'Support Cost Reduction' },
      ],
      testimonial: 'The AI chatbot handles thousands of customer inquiries daily, freeing our team to focus on complex issues.',
      author: 'Sarah Johnson, CTO',
      color: 'from-purple-500 to-pink-500',
    },
    {
      company: 'HealthCare Systems',
      industry: 'Healthcare',
      logo: '/api/placeholder/120/60',
      challenge: 'Legacy systems limiting innovation, compliance tracking issues, and slow deployment cycles.',
      solution: 'Complete digital transformation with cloud migration, API-first architecture, and automated compliance monitoring.',
      results: [
        { icon: TrendingUp, value: '10x', label: 'Deployment Speed' },
        { icon: DollarSign, value: '60%', label: 'Infrastructure Cost Cut' },
        { icon: Award, value: '100%', label: 'Compliance Rate' },
      ],
      testimonial: 'mwbTechs guided us through our entire digital transformation journey with expertise and patience.',
      author: 'Dr. Michael Chen, Chief Innovation Officer',
      color: 'from-green-500 to-emerald-500',
    },
    {
      company: 'FinTech Innovations',
      industry: 'Finance',
      logo: '/api/placeholder/120/60',
      challenge: 'Needed custom trading platform with real-time data processing and compliance with financial regulations.',
      solution: 'Custom development of high-performance trading platform with advanced analytics, real-time processing, and regulatory compliance.',
      results: [
        { icon: TrendingUp, value: '<50ms', label: 'Latency' },
        { icon: Users, value: '100K+', label: 'Daily Active Users' },
        { icon: DollarSign, value: '$10M+', label: 'Transaction Volume' },
      ],
      testimonial: 'They built exactly what we needed, on time and within budget. The platform performs flawlessly.',
      author: 'Emily Rodriguez, CEO',
      color: 'from-orange-500 to-red-500',
    },
  ];

  const industries = ['All Industries', 'Manufacturing', 'Retail', 'Healthcare', 'Finance', 'Technology'];

  return (
    <>
      <Helmet>
        <title>Case Studies - Success Stories | mwbTechs</title>
        <meta name="description" content="Read how companies across industries achieved success with mwbTechs." />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-6">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">Case Studies</h1>
              <p className="text-xl text-gray-600">Real companies, real results, real transformation</p>
            </motion.div>
          </div>
        </section>

        <section className="section">
          <div className="container-custom">
            {/* Industry Filter */}
            <div className="flex flex-wrap gap-4 justify-center mb-12">
              {industries.map((industry, index) => (
                <button
                  key={index}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    index === 0
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 border border-gray-300 hover:border-primary-300'
                  }`}
                >
                  {industry}
                </button>
              ))}
            </div>

            {/* Case Studies */}
            <div className="space-y-12">
              {caseStudies.map((study, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-lg"
                >
                  {/* Header */}
                  <div className={`bg-gradient-to-r ${study.color} p-8 text-white`}>
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                      <div>
                        <h2 className="text-3xl font-bold mb-2">{study.company}</h2>
                        <span className="inline-block bg-white/20 px-4 py-1 rounded-full text-sm">
                          {study.industry}
                        </span>
                      </div>
                      <div className="bg-white rounded-lg p-4 h-16 flex items-center justify-center">
                        <div className="text-gray-400 text-sm font-medium">Logo</div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <div className="grid lg:grid-cols-2 gap-8 mb-8">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">The Challenge</h3>
                        <p className="text-gray-600 leading-relaxed">{study.challenge}</p>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">The Solution</h3>
                        <p className="text-gray-600 leading-relaxed">{study.solution}</p>
                      </div>
                    </div>

                    {/* Results */}
                    <div className="bg-gray-50 rounded-xl p-8 mb-8">
                      <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Results</h3>
                      <div className="grid md:grid-cols-3 gap-6">
                        {study.results.map((result, i) => {
                          const Icon = result.icon;
                          return (
                            <div key={i} className="text-center">
                              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                                <Icon className="w-6 h-6 text-primary-600" />
                              </div>
                              <div className="text-4xl font-bold text-primary-600 mb-2">{result.value}</div>
                              <div className="text-gray-600">{result.label}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Testimonial */}
                    <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-6 border border-primary-100">
                      <p className="text-lg text-gray-700 italic mb-4">"{study.testimonial}"</p>
                      <p className="text-sm font-semibold text-gray-900">— {study.author}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Load More */}
            <div className="text-center mt-12">
              <button className="btn btn-outline text-lg">
                Load More Case Studies
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-sm bg-gradient-primary text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Write Your Success Story?</h2>
            <p className="text-xl text-blue-100 mb-8">Join hundreds of companies transforming their operations</p>
            <div className="flex gap-4 justify-center">
              <Link to="/signup" className="btn btn-white text-lg">
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/contact" className="btn btn-outline text-white border-white hover:bg-white hover:text-primary-600 text-lg">
                Schedule Demo
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CaseStudiesPage;
